// App.js
import React, { lazy, Suspense, useEffect, useState, useMemo } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import Navbar from "./Components/Home/Navbar";
import "antd/dist/reset.css";
import heroBg from "./assets/images/background1.jpg";
import { WhatsAppOutlined } from "@ant-design/icons";
import { Button, Spin } from "antd";

import PrivateRoute from "./PrivateRoute";
import { useDispatch } from "react-redux";
import { refreshToken } from "./redux/slices/user";
import { API_BASE } from "./utils/config";
import { videoInterviewRequest } from "./utils/videoInterviews";

// Load only the page the visitor opens. This keeps large admin tables, charts,
// and portals out of the registration/login startup path.
const Home = lazy(() => import("./Components/Home/Home"));
const Login = lazy(() => import("./Components/Signup&Login/Login"));
const SignupSteps = lazy(() => import("./Components/Signup&Login/SignupSteps"));
const BackupFooter = lazy(() => import("./Components/Home/BackupFooter"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Crew = lazy(() => import("./pages/Crew"));
const Companies = lazy(() => import("./pages/Companies"));
const Interviews = lazy(() => import("./pages/Interviews"));
const CrewProfile = lazy(() => import("./pages/CrewProfile"));
const Staff = lazy(() => import("./pages/Staff"));
const Statuses = lazy(() => import("./pages/Statuses"));
const CrewAnalytics = lazy(() => import("./pages/CrewAnalytics"));
const BackupCrew = lazy(() => import("./pages/BackupCrew"));
const TagsPage = lazy(() => import("./pages/Tags"));
const JobsPage = lazy(() => import("./pages/Jobs"));
const JobTypesPage = lazy(() => import("./pages/JobTypes"));
const ShiftsPage = lazy(() => import("./pages/Shifts"));
const PaymentRulesPage = lazy(() => import("./pages/PaymentRules"));
const PaymentsPage = lazy(() => import("./pages/Payments"));
const SurveysPage = lazy(() => import("./pages/Surveys"));
const SurveyBuilder = lazy(() => import("./pages/SurveyBuilder"));
const SurveyInsights = lazy(() => import("./pages/SurveyInsights"));
const PublicSurvey = lazy(() => import("./pages/PublicSurvey"));
const CompanyDashboard = lazy(() => import("./pages/company/CompanyDashboard"));
const CompanyRequests = lazy(() => import("./pages/company/CompanyRequests"));
const CompanyJobs = lazy(() => import("./pages/company/CompanyJobs"));
const CompanySurveys = lazy(() => import("./pages/company/CompanySurveys"));
const CompanyReports = lazy(() => import("./pages/company/CompanyReports"));
const CompanyProfile = lazy(() => import("./pages/company/CompanyProfile"));
const CompanyJobRequests = lazy(() => import("./pages/CompanyJobRequests"));
const VideoInterviews = lazy(() => import("./pages/VideoInterviews"));
const CrewVideoInterview = lazy(() => import("./pages/CrewVideoInterview"));

/* ---------------- Helpers ---------------- */
const getAccess = () => localStorage.getItem("access");
const getRefresh = () => localStorage.getItem("refresh");

const isExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

const isAuthed = () => {
  const t = getAccess();
  return t && t !== "null" && t !== "undefined" && String(t).trim() !== "";
};
const roleLanding = () => {
  const role = (localStorage.getItem("role") || "").toLowerCase();
  return role === "company" ? "/company/dashboard" : role === "crew" ? "/crewdash" : "/dashboard";
};

function App() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  // Block initial render of private routes until we finish one auth pass
  const [authReady, setAuthReady] = useState(false);
  const [interviewGate, setInterviewGate] = useState({ token: null, checking: false, required: false });

  useEffect(() => {
    const init = async () => {
      const access = getAccess();
      const refresh = getRefresh();

      // If we have no refresh at all, nothing to do
      if (!refresh) {
        setAuthReady(true);
        return;
      }

      // If access missing or expired, try a single refresh
      if (!access || isExpired(access)) {
        try {
          await dispatch(refreshToken()).unwrap();
        } catch {
          // refresh failed → tokens already cleared in slice
        }
      }
      const activeAccess = getAccess();
      if (activeAccess) {
        try {
          const meResponse = await fetch(`${API_BASE}/auth/me/`, { headers: { Authorization: `Bearer ${activeAccess}` } });
          if (meResponse.ok) {
            const me = await meResponse.json();
            if (me.role) localStorage.setItem("role", me.role);
            if (me.company) localStorage.setItem("company", JSON.stringify(me.company));
          }
        } catch (_) {}
      }
      setAuthReady(true);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const activeAccess = getAccess();
  const activeRole = (localStorage.getItem("role") || "").toLowerCase();

  useEffect(() => {
    if (!authReady) return undefined;
    if (!activeAccess || activeRole !== "crew") {
      setInterviewGate({ token: activeAccess || null, checking: false, required: false });
      return undefined;
    }

    let cancelled = false;
    setInterviewGate({ token: activeAccess, checking: true, required: false });
    videoInterviewRequest("/video-interview/me/")
      .then((result) => {
        if (cancelled) return;
        setInterviewGate({
          token: getAccess(),
          checking: false,
          required: result?.required === true || result?.attempt?.status === "in_progress",
        });
      })
      .catch(() => {
        if (cancelled) return;
        // A crew member must not bypass a mandatory interview when its status
        // cannot be verified. The interview screen can display the API error.
        setInterviewGate({ token: getAccess(), checking: false, required: true });
      });

    return () => { cancelled = true; };
  }, [activeAccess, activeRole, authReady]);

  useEffect(() => {
    const handleInterviewStatus = (event) => {
      const status = event?.detail?.status;
      if (!status) return;
      setInterviewGate({
        token: getAccess(),
        checking: false,
        required: status === "in_progress",
      });
    };
    window.addEventListener("backup:video-interview-status", handleInterviewStatus);
    return () => window.removeEventListener("backup:video-interview-status", handleInterviewStatus);
  }, []);

  const isDashboardRoute = useMemo(
    () =>
      ["/dashboard", "/crew", "/crewanalytics", "/backupcrew", "/companies", "/interviews", "/video-interview", "/staff", "/crewdash", "/statuses", "/tags", "/jobs", "/job-types", "/shifts", "/payment-rules", "/payments", "/surveys", "/company"].some((p) =>
        pathname.startsWith(p)
      ),
    [pathname]
  );

  const isRegistrationRoute = useMemo(
    () => ["/login", "/register"].some((p) => pathname.startsWith(p)),
    [pathname]
  );

  const isPublicSurveyRoute = pathname.startsWith("/s/");

  const crewGatePending = Boolean(
    authReady
    && activeAccess
    && activeRole === "crew"
    && (interviewGate.checking || interviewGate.token !== activeAccess)
  );
  const mandatoryCrewInterview = Boolean(
    authReady
    && activeAccess
    && activeRole === "crew"
    && interviewGate.token === activeAccess
    && interviewGate.required
  );

  // Full-screen gates prevent private content flashing before auth and the
  // mandatory-interview status checks have completed.
  if (!authReady || crewGatePending) {
    return (
      <div
        style={{
          display: "grid",
          placeItems: "center",
          minHeight: "100vh",
          backgroundImage: `url(${heroBg})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (mandatoryCrewInterview && pathname !== "/video-interview") {
    return <Redirect to="/video-interview" />;
  }

  return (
    <div
      className="App"
      style={{
        backgroundImage: `url(${heroBg})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "cover",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Navbar only on public routes */}
      {!isDashboardRoute && !isPublicSurveyRoute && <Navbar />}

      <Suspense fallback={<div style={{ display: "grid", minHeight: "45vh", placeItems: "center" }}><Spin size="large" /></div>}>
      <Switch>
        <Route exact path="/home" component={Home} crewRedirect={true} />
        <Route exact path="/login" component={Login} crewRedirect={true} />
        <Route exact path="/register" component={SignupSteps} crewRedirect={true} />
        <Route exact path="/s/:publicId" component={PublicSurvey} />

        {/* Private */}
        <PrivateRoute exact path="/crewdash" component={CrewProfile} />
        <PrivateRoute exact path="/video-interview" component={CrewVideoInterview} allowedRoles={["crew"]} />
        <PrivateRoute exact path="/dashboard" component={Dashboard} crewRedirect={true} />
        <PrivateRoute exact path="/crewanalytics" component={CrewAnalytics} crewRedirect={true} />
        <PrivateRoute exact path="/backupcrew" component={BackupCrew} crewRedirect={true} />
        <PrivateRoute exact path="/crew" component={Crew} crewRedirect={true} />
        <PrivateRoute exact path="/staff" component={Staff} crewRedirect={true} />
        <PrivateRoute exact path="/companies" component={Companies} crewRedirect={true} />
        <PrivateRoute exact path="/interviews" component={Interviews} crewRedirect={true} />
        <PrivateRoute exact path="/video-interviews" component={VideoInterviews} allowedRoles={["admin", "staff"]} />
        <PrivateRoute exact path="/jobs" component={JobsPage} crewRedirect={true} />
        <PrivateRoute exact path="/job-types" component={JobTypesPage} crewRedirect={true} />
        <PrivateRoute exact path="/shifts" component={ShiftsPage} crewRedirect={true} />
        <PrivateRoute exact path="/payment-rules" component={PaymentRulesPage} crewRedirect={true} />
        <PrivateRoute exact path="/payments" component={PaymentsPage} crewRedirect={true} />
        <PrivateRoute exact path="/company-requests" component={CompanyJobRequests} allowedRoles={["admin", "staff"]} />
        <PrivateRoute exact path="/surveys" component={SurveysPage} crewRedirect={true} />
        <PrivateRoute exact path="/surveys/:id/builder" component={SurveyBuilder} crewRedirect={true} />
        <PrivateRoute exact path="/surveys/:id/insights" component={SurveyInsights} crewRedirect={true} />
        <PrivateRoute exact path="/company/dashboard" component={CompanyDashboard} allowedRoles={["company"]} />
        <PrivateRoute exact path="/company/requests" component={CompanyRequests} allowedRoles={["company"]} />
        <PrivateRoute exact path="/company/jobs" component={CompanyJobs} allowedRoles={["company"]} />
        <PrivateRoute exact path="/company/surveys" component={CompanySurveys} allowedRoles={["company"]} />
        <PrivateRoute exact path="/company/surveys/:id/builder" component={SurveyBuilder} allowedRoles={["company"]} />
        <PrivateRoute exact path="/company/surveys/:id/insights" component={SurveyInsights} allowedRoles={["company"]} />
        <PrivateRoute exact path="/company/reports" component={CompanyReports} allowedRoles={["company"]} />
        <PrivateRoute exact path="/company/profile" component={CompanyProfile} allowedRoles={["company"]} />
        <PrivateRoute exact path="/statuses" component={Statuses} crewRedirect={true} />
        <PrivateRoute exact path="/tags" component={TagsPage} crewRedirect={true} />

        {/* Fallback */}
        <Route
          path="*"
          render={() => (isAuthed() ? <Redirect to={roleLanding()} /> : <Redirect to="/home" />)}
        />
      </Switch>

      {!isRegistrationRoute && !isDashboardRoute && !isPublicSurveyRoute && <BackupFooter />}
      </Suspense>

      {/* Login and registration include contextual support links inside the form. */}
      {!isRegistrationRoute && !isPublicSurveyRoute && !mandatoryCrewInterview && (
        <a
          href="https://wa.me/962792424141"
          target="_blank"
          rel="noopener noreferrer"
          style={{ position: "fixed", bottom: 20, left: 20, zIndex: 1000 }}
        >
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<WhatsAppOutlined />}
            style={{ backgroundColor: "#25D366", border: "none", boxShadow: "0 4px 10px rgba(0,0,0,0.3)" }}
          />
        </a>
      )}
    </div>
  );
}

export default App;
